import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import { withFormik, Form } from 'formik';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import _ from 'lodash';

import PropTypes from 'prop-types';
import { ErrorHandler, captureException } from '@playbookux/playbook-web-core';
import TestTypes from '../../../core/constants/testTypes';
import Devices from '../../../core/constants/device-types';
import { camelCase } from '../../../core/utils/camel-case';
import Graphql from '../../../core/services/graphql';

import {
  Container,
  TextError,
  Loader,
  Message,
} from './styles';

import { checkToken, createRecruitToken } from './requests';
import schema from './schema';

import { withTranslation } from '../../../core/i18n';
import withIntercom from '../../../core/components/with-intercom';
import {
  clearQuestions,
  setProjectValues,
  setTestSession,
  signInWithTester,
  authenticateWithScreener,
  signOut,
} from '../../../core/actions';
import { GET_SCREENER_SESSION } from '../../../core/actions/screener-graphql';
import FormMobile from './form-mobile';
import FormComputer from './form-computer';
import { handleRedirectSession } from '../../utils/handleRedirectSession';
import ParticipantsSource from '../../../core/constants/participantsSource';

const RecruitParticipants = (props) => {
  const {
    handleSubmit,
    location = {},
    setFieldValue,
    values,
    status,
    setLocale,
    onClearQuestions,
    onSetProjectValues,
    authentication,
    onSignOut,
  } = props;
  const { search } = location;

  const [isSubmittingModeratedProject, setIsSubmittingModeratedProject] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [device, setDevice] = useState('');

  // regex to check if is running on mobile device (tablet or smarthphone)
  const isMobile = () => (
    /Mobile|mobile|Android/.test(navigator.userAgent)
  );

  const handleCheckToken = useCallback(async () => {
    const { token } = queryString.parse(search);
    let isModerated = false;
    try {
      const response = await checkToken(token);

      // Return the project data or the message error.
      const { project, segment: responseSegment, message } = response;

      _.forIn(project, (value, key) => {
        const projectKey = project[key];
        delete project[key];
        project[camelCase(key)] = projectKey;
      });

      // Update project with correct device and participantsLanguage
      // which are stored on the segment
      project.device = responseSegment?.device;
      project.participantsLanguage = responseSegment?.participants_language;
      project.operatingSystem = responseSegment?.operating_system;

      if (!_.isEmpty(project) && !message) {
        // Set the initial values of the project in the redux.
        onSetProjectValues(project);
        setFieldValue('project', project);
        setFieldValue('segment', responseSegment);
        setLocale(project.participantsLanguage);
        setDevice(project.device);

        if (project.testType === TestTypes.MODERATED) {
          isModerated = true;
          setFieldValue('firstName', 'Screener');
          setFieldValue('lastName', 'Session');
          /**
           * Since setFieldValue does not immediately reflect changes, we use the isSubmittingModeratedProject state with an useEffect
           * to track when we are submitting moderated projects, avoiding workarounds like useTimeout.
           */
          setIsSubmittingModeratedProject(true);
        }
      } else if (message) {
        setError({ message });
      }
    } catch (err) {
      setError(err);
      captureException('HANDLE_CHECK_TOKEN_FAILED', err);
    }

    if (!isModerated) {
      setIsLoading(false);
    }
  }, [onSetProjectValues, search, setFieldValue, setLocale, setDevice]);

  /**
   * Tester must be signed out because a new tester is created for guest sessions
   */
  const handleSignOutUser = () => {
    if (!authentication.isRecruitUser || authentication.isAuthenticated) {
      onSignOut();
    }
  };

  /**
   * Ensure that we have the necessary data and can submit the moderated project without field errors.
   */
  useEffect(() => {
    if (isSubmittingModeratedProject) {
      setTimeout(async () => {
        await handleSubmit();
      }, 500);
    }
  }, [isSubmittingModeratedProject]);

  /**
   * Works as componentDidMount
   */
  useEffect(() => {
    // Used to clear all the Tester answered questions in the redux before the survey starts.
    onClearQuestions();
    handleSignOutUser();
    handleCheckToken();
  }, []);

  if (error || (status && status.error)) {
    if (error && error.code === 'maximum_tests_reached') {
      return (
        <Container center>
          <Message>{error.message}</Message>
        </Container>
      );
    }

    if (error && error.code === 'test_session_expired') {
      return (
        <Container center>
          <Message>{error.message}</Message>
        </Container>
      );
    }

    return (
      <Container center>
        <ErrorHandler
          error={error || status.error}
          defaultMessage="Something went wrong. Verify if the URL is correct."
        />
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Loader data-loader="recruit-loader" />
    );
  }

  if (values && values.project && values.project.recorderType === 'chrome_extension' && isMobile()) {
    let deviceType = 'smartphone';
    if (window.innerWidth >= 540) {
      deviceType = 'tablet';
    }
    return (
      <Container center>
        <TextError>
          {`You're using the wrong device! Please take this study on a computer, not a ${deviceType}.`}
        </TextError>
      </Container>
    );
  }

  const { participantsSource, company } = values.project;

  const isRYO = participantsSource === ParticipantsSource.COMPANY;
  const collectTesterName = !(isRYO && !company.collect_byo_testers_info);

  return (
    <Container>
      {device !== Devices.COMPUTER ? (
        <Form>
          <FormMobile
            onClick={handleSubmit}
            status={status}
            collectTesterName={collectTesterName}
          />
        </Form>
      ) : (
        <FormComputer
          onClick={handleSubmit}
          project={values.project}
          status={status}
          collectTesterName={collectTesterName}
        />
      )}
    </Container>
  );
};

RecruitParticipants.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  status: PropTypes.shape({
    error: PropTypes.shape({
      message: PropTypes.string,
    }),
  }),
  handleSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setLocale: PropTypes.func.isRequired,
  values: PropTypes.shape({
    project: PropTypes.shape({
      testType: PropTypes.string,
      recorderType: PropTypes.string,
      participantsSource: PropTypes.string,
      company: PropTypes.shape({
        collect_byo_testers_info: PropTypes.bool,
      }),
    }).isRequired,
  }).isRequired,
  onClearQuestions: PropTypes.func.isRequired,
  onSetProjectValues: PropTypes.func.isRequired,
  onSignOut: PropTypes.func.isRequired,
  authentication: PropTypes.shape({
    token: PropTypes.string,
    isRecruitUser: PropTypes.bool,
    isAuthenticate: PropTypes.bool,
    isAuthenticated: PropTypes.bool,
  }).isRequired,
};

RecruitParticipants.defaultProps = {
  status: {
    error: undefined,
    isLoading: false,
  },
};

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    onSignInWithTester: signInWithTester,
    onClearQuestions: clearQuestions,
    onSetProjectValues: setProjectValues,
    onSetTestSession: setTestSession,
    onAuthenticateWithScreener: authenticateWithScreener,
    onSignOut: signOut,
  },
  dispatch,
);

const mapStateToProps = ({ authentication }) => ({
  authentication,
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withFormik({
    validationSchema: schema,
    mapPropsToValues: () => ({
      firstName: '',
      lastName: '',
      query: Graphql,
    }),
    handleSubmit: async (values, { props, setStatus }) => {
      const {
        onSignInWithTester,
        onSetTestSession,
        history,
      } = props;

      const {
        project,
        firstName,
        lastName,
        query,
        segment,
      } = values;

      const {
        device,
        participantsLanguage: language,
        recorderType,
        testType,
        projectConsentForms,
      } = project;

      const { authentication, onAuthenticateWithScreener } = props;

      const consentForms = _.map(projectConsentForms, ({ consent_form: cf }) => cf);
      const hasConsentForms = !_.isEmpty(consentForms);
      const isPlaybookTester = authentication && authentication.token && authentication.scope !== 'public';

      const { token } = queryString.parse(props.location.search);

      if (testType === 'moderated' && isPlaybookTester) {
        let response = {};
        if (!authentication.sessionId) {
          response = await createRecruitToken(token, '', '', authentication.user.id);
        }

        const {
          screener_session: screenersSession,
          test_session: testSession,
        } = response;

        if (screenersSession) {
          const { data = {} } = await query.query({
            query: GET_SCREENER_SESSION,
            variables: {
              sessionId: authentication.sessionId || response.screener_session.id,
            },
          });

          const {
            screenerSession,
            tester,
          } = data;

          if (data && screenerSession && token === screenerSession.tester.segment.recruitParticipantToken.token) {
            if (screenerSession.tester && screenerSession.tester.status === 'pending') {
              const sessionId = authentication.sessionId || response.screener_session.id;
              const redirectPath = `/screener-session?session-id=${sessionId}`
                + `&token=${screenerSession.authenticationToken}&lang=${language}`;
              if (hasConsentForms) {
                props.history.push(`/consent-form?testerId=${screenerSession.tester.id}#${redirectPath}`);
              } else {
                props.history.push(redirectPath);
              }
            } else if (screenerSession.tester && screenerSession.tester.status === 'disqualified') {
              if (!tester.project.disqualifyRedirectUrl) {
                props.history.push('/screener-session/disqualified');
              } else {
                handleRedirectSession(tester.project.disqualifyRedirectUrl, tester.project.hasMrId, tester.mrid);
              }
            } else {
              const newSearch = queryString.stringify({
                segment_id: screenerSession.tester.segment.id,
                participants_source: screenerSession.tester.project.participantsSource,
              });
              props.history.push({ pathname: '/schedule', search: newSearch });
            }
            return;
          }
        } else if (testSession) {
          const {
            tester_id: testerId,
          } = testSession;

          // Set the values of the current test session in the redux.
          onSetTestSession(testSession);

          const redirectPath = `/schedule?segment_id=${segment.id}`;
          history.push(`/consent-form?testerId=${testerId}#${redirectPath}`);
          return;
        }
      }

      const hasWelcomePage = testType === TestTypes.CARD_SORT || testType === TestTypes.TREE_TEST || testType === TestTypes.SURVEY;

      setStatus({
        isLoading: true,
      });

      try {
        let response;
        if (testType === 'moderated') {
          response = await createRecruitToken(token, '', '');
        } else {
          response = await createRecruitToken(token, firstName, lastName);
        }

        const {
          screener_session: screenersSession,
          test_session: testSession,
        } = response;

        if (hasConsentForms) {
          if (screenersSession) {
            const {
              id,
              authentication_token: authenticationToken,
              tester_id: testerId,
            } = screenersSession;

            await onAuthenticateWithScreener(id, authenticationToken, true);

            const redirectPath = `/screener-session?session-id=${id}`
            + `&token=${authenticationToken}&lang=${language}`;

            history.push(`/consent-form?testerId=${testerId}#${redirectPath}`);
          } else if (testSession) {
            const {
              id,
              authentication_token: authenticationToken,
              tester_id: testerId,
            } = testSession;

            // Set the values of the current test session in the redux.
            onSetTestSession(testSession);

            await onSignInWithTester(id, authenticationToken, true);

            let redirectPath;
            if (hasWelcomePage) {
              redirectPath = `/welcome?session-id=${id}`;
            } else if (testType === 'moderated') {
              redirectPath = `/schedule?segment_id=${segment.id}`;
              history.push(`/consent-form?testerId=${testerId}#${redirectPath}`);
            } else {
              redirectPath = `/unmoderated-session?session-id=${id}&token=${authenticationToken}`
                + `&device=${device}&recorder=${recorderType}&lang=${language}`;
            }

            history.push(`/consent-form?testerId=${testerId}#${redirectPath}`);
          }
        } else if (screenersSession) {
          const pathScreenerSession = `/screener-session?session-id=${screenersSession.id}`;
          const pathToken = `&token=${screenersSession.authentication_token}&lang=${language}`;
          const redirectPathScreenerSession = `${pathScreenerSession}${pathToken}`;

          history.push(
            redirectPathScreenerSession,
          );
        } else if (testSession) {
          const {
            id,
            authentication_token: authenticationToken,
          } = testSession;

          // Set the values of the current test session in the redux.
          onSetTestSession(testSession);

          await onSignInWithTester(testSession.id, authenticationToken, true);

          if (hasWelcomePage) {
            history.push(`/welcome?session-id=${testSession.id}`);
          } else {
            history.push(
              `/unmoderated-session?session-id=${id}&token=${authenticationToken}`
              + `&device=${device}&recorder=${recorderType}&lang=${language}`,
            );
          }
        }
      } catch (error) {
        setStatus({
          error,
        });
      }
    },
  }),
  withRouter,
  withIntercom,
  withTranslation,
)(RecruitParticipants);
